import {
  ICreateSpecialOfferParams,
  IPaymentService,
  ISpecialOffer,
  ISpecialOfferDisplay,
  NotFoundError,
} from 'fitbeat.models';
import { AccountStatus } from 'fitbeat.models';
import moment from 'moment-timezone';
import uuidv4 from 'uuid/v4';
import { UserProfile } from '../../entities';
import { IUserProfileRepository } from '../../persistence';
import { DynamicLinkService } from '../dynamic-link';

export const OFFER_EXPIRES_DAYS_AFTER_BILLING_START = 30;

export class SpecialOfferManager {
  constructor(
    private userProfileRepository: IUserProfileRepository,
    private dynamicLinkService: DynamicLinkService,
    private paymentService: IPaymentService,
  ) {
  }

  public async createSpecialOffer(
    uid: string,
    params: ICreateSpecialOfferParams,
  ): Promise<ISpecialOfferDisplay> {
    const userProfile = await this.userProfileRepository.getUserProfileOrThrow(uid);

    const billingStartDate = new Date(params.billingStartDate).toISOString();
    const offerExpiryDate = this.calculateExpiryDate(billingStartDate);
    const specialOffer: ISpecialOffer = {
      id: uuidv4(),
      createdDate: new Date().toISOString(),
      contactPlanId: params.contactPlanId,
      billingStartDate,
      offerExpiryDate,
      contractCoupon: params.contractCoupon,
    };
    userProfile.specialOffers =
      userProfile.specialOffers ?
        userProfile.specialOffers.concat(specialOffer) :
        [specialOffer];

    await this.userProfileRepository.update(userProfile);

    return this.mapSpecialOffer(uid, specialOffer);
  }

  public async updateSpecialOffer(
    uid: string,
    specialOfferId: string,
    params: ICreateSpecialOfferParams,
  ): Promise<ISpecialOfferDisplay> {
    const userProfile = await this.userProfileRepository.getUserProfileOrThrow(uid);
    const specialOfferToUpdate = userProfile.specialOffers?.find((offer) => offer.id === specialOfferId);
    if (!specialOfferToUpdate) {
      throw new NotFoundError('updateSpecialOffer', specialOfferId);
    }

    const billingStartDate = new Date(params.billingStartDate).toISOString();
    const offerExpiryDate = this.calculateExpiryDate(billingStartDate);

    const updatedOffer: ISpecialOffer = {
      billingStartDate,
      offerExpiryDate,
      contractCoupon: params.contractCoupon,
      contactPlanId: params.contactPlanId,
      id: specialOfferToUpdate.id,
      createdDate: specialOfferToUpdate.createdDate,
    };

    userProfile.specialOffers = userProfile.specialOffers?.map((offer) => {
      if (offer.id === specialOfferId) {
        return updatedOffer;
      }
      return offer;
    });

    await this.userProfileRepository.update(userProfile);
    return this.mapSpecialOffer(uid, updatedOffer);
  }

  public getSpecialFromUser(userProfile: UserProfile, specialOfferId: string): ISpecialOffer {
    if (!userProfile.specialOffers) {
      throw new Error(`User: ${userProfile.uid} does not have any special offers`);
    }

    const specialOffer = userProfile.specialOffers.find((offer) => offer.id === specialOfferId);
    if (!specialOffer) {
      throw new Error(`Special offer: ${specialOfferId} not found`);
    }

    return specialOffer;
  }

  public getActiveSpecialOffer(
    userProfile: UserProfile,
  ): ISpecialOffer | undefined {
    const now = new Date();
    return userProfile.specialOffers?.find((offer) => this.isSpecialOfferValid(offer, now));
  }

  public async getActiveSpecialOfferForDisplay(
    userProfile: UserProfile,
  ): Promise<ISpecialOfferDisplay | undefined> {
    const activeSpecialOffer = this.getActiveSpecialOffer(userProfile);
    if (!activeSpecialOffer) {
      return;
    }
    return this.mapSpecialOffer(userProfile.uid, activeSpecialOffer);
  }

  public isSpecialOfferValid(specialOffer: ISpecialOffer, date: Date): boolean {
    const offerExpireDate = new Date(specialOffer.offerExpiryDate);
    return offerExpireDate > date;
  }

  public isSpecialOfferEnabled(currentAccountStatus: AccountStatus): boolean {
    return currentAccountStatus !== 'StudioContract' && currentAccountStatus !== 'LiteSubscription';
  }

  private async mapSpecialOffer(
    uid: string,
    specialOffer: ISpecialOffer,
  ): Promise<ISpecialOfferDisplay> {
    const subscriptionSummary = await this.paymentService.getPaymentSummaryFromPlanAndCoupon(
      specialOffer.contactPlanId,
      specialOffer.contractCoupon,
    );
    return {
      ...specialOffer,
      couponDescription: subscriptionSummary.coupon?.name || undefined,
      couponAmountOff: subscriptionSummary.coupon?.amount_off || undefined,
      couponPercentOff: subscriptionSummary.coupon?.percent_off || undefined,
      planDescription: subscriptionSummary.name,
      totalValueBeforeDiscount: subscriptionSummary.amount,
      interval: subscriptionSummary.interval,
      totalChargedToCustomer: subscriptionSummary.total,
      currencySymbol: subscriptionSummary.currencySymbol,
      shortLink: await this.dynamicLinkService.createSpecialOfferLink(uid),
    };
  }

  private calculateExpiryDate(utcDateString: string): string {
    return moment(utcDateString).add(OFFER_EXPIRES_DAYS_AFTER_BILLING_START, 'days').toISOString();
  }
}
